/**
 * <PageEmptyState/> — shared empty/error/no-match state.
 *
 * Bordered-dashed card with tinted icon-tile, title, hint, optional actions.
 * Pass `tint` to override the icon/CTA accent color (defaults to `button/primary`).
 */
import type { CSSProperties, ReactElement, ReactNode } from 'react';

import * as styles from './page-empty-state.css';

interface PageEmptyStateProps {
  icon: ReactNode;
  title: string;
  hint?: string;
  action?: ReactNode;
  actions?: ReactNode;
  testId?: string;
  variant?: 'full' | 'card';
  /** CSS color string. Sets `--empty-tint` on the root. */
  tint?: string;
}

export function PageEmptyState({
  icon,
  title,
  hint,
  action,
  actions,
  testId,
  variant = 'full',
  tint,
}: PageEmptyStateProps): ReactElement {
  const style = tint
    ? ({ ['--empty-tint' as string]: tint } as CSSProperties)
    : undefined;
  return (
    <div
      className={variant === 'card' ? styles.emptyStateCard : styles.emptyState}
      style={style}
      data-testid={testId}
    >
      <span className={styles.emptyIcon}>{icon}</span>
      <h3 className={styles.emptyTitle}>{title}</h3>
      {hint && <p className={styles.emptyHint}>{hint}</p>}
      {actions ? <div className={styles.emptyActions}>{actions}</div> : action}
    </div>
  );
}

export {
  emptyAction as pageEmptyActionClass,
  emptySecondaryAction as pageEmptySecondaryActionClass,
} from './page-empty-state.css';
